import React, { useContext } from 'react';
import { PetAvatar, AvatarSizes } from '@chewy/pet-profile-pet-avatar';
import { PetInterface } from 'types/pet';
import { KibButtonNew } from '@chewy/kib-controls-react';
import {
  getPetEditPath,
  getPetManagePath,
  getPetProfilePath,
  contactPath,
} from '../../../util/config';
import AnalyticsContext from '../../../context/AnalyticsContext';
import useLocationQuery from '../../../hooks/useLocationQuery';
import { getSharedCopy, getUndoConfirmationCopy } from '../copy';
import './manage-pet-undo-confirmation.scss';

export type ManagePetUndoConfirmationProps = {
  pet: PetInterface;
};

export const ManagePetUndoConfirmation = ({ pet }: ManagePetUndoConfirmationProps) => {
  const { headerText, bodyText, profileButtonText, memorializeText } = getUndoConfirmationCopy(pet);
  const { contactPrompt, contactLinkText } = getSharedCopy(pet);

  let query = useLocationQuery();
  const redirectUrl = query.get('redirectUrl');
  const analytics = useContext(AnalyticsContext);

  const memorialLink = redirectUrl
    ? getPetManagePath(pet.id) +
      `?redirectUrl=${encodeURIComponent(redirectUrl)}` +
      `&innerRedirectUrl=${encodeURIComponent(getPetEditPath(pet.id))}`
    : getPetManagePath(pet.id);

  const goToProfile = (petId: number) => {
    if (redirectUrl) {
      window.location.href =
        getPetProfilePath(petId) + `?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    } else {
      window.location.href = getPetProfilePath(petId);
    }
  };

  return (
    <div className="manage-pet-undo-confirmation" data-testid="manage-pet-undo-confirmation">
      <PetAvatar src={pet.avatarUrl} size={AvatarSizes.MEDIUM} />

      <p
        className="manage-pet-undo-confirmation__header"
        data-testid="manage-pet-undo-confirmation__header"
      >
        {`${headerText}`}
      </p>
      <p
        className="manage-pet-undo-confirmation__body"
        data-testid="manage-pet-undo-confirmation__body"
      >
        {`${bodyText}`}
      </p>
      <div className="manage-pet-undo-confirmation__action-buttons">
        <KibButtonNew
          className="manage-pet-undo-confirmation__profile-ingress"
          data-testid="manage-pet-undo-confirmation__profile-ingress"
          emphasis="primary"
          size="medium"
          onClick={() => goToProfile(pet.id)}
        >
          {profileButtonText}
        </KibButtonNew>
        <KibButtonNew
          className="manage-pet-undo-confirmation__memorialize-ingress"
          data-testid="manage-pet-undo-confirmation__memorialize-ingress"
          emphasis="tertiary"
          size="medium"
          onClick={() => {
            window.location.href = memorialLink;
            analytics.trackRainbow({
              name: 'pet profile rainbow - clicked',
              category: 'pet-profile-rainbow',
              action: 'click',
              label: 'pet_profile_rainbow_restored_memorialize',
            });
          }}
        >
          {memorializeText}
        </KibButtonNew>
      </div>
      <div className="manage-pet-undo-confirmation__support">
        <span
          className="manage-pet-undo-confirmation__support-prompt"
          data-testid="manage-pet-undo-confirmation__support-prompt"
        >
          {contactPrompt}
        </span>
        <a
          className="manage-pet-undo-confirmation__support-link"
          data-testid="manage-pet-undo-confirmation__support-link"
          href={contactPath}
          target="_blank"
          rel="noreferrer"
        >
          {contactLinkText}
        </a>
      </div>
    </div>
  );
};
